import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Post, PostTarget, PostTargetChildScope } from '../../model/post';
import { PostService } from '../../service/data/post.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { PropertyPostTarget } from '../../../rent/model/property';
import { CompanyService } from '../../../rent/service/data/company.service';
import { CompanyPostTarget } from '../../../rent/model/company';
import { PropertyService } from '../../../rent/service/data/property.service';
import { BuildingService } from '../../../rent/service/data/building.service';
import { BuildingPostTarget } from '../../../rent/model/building';
import { DwellingPostTarget } from '../../../rent/model/dwelling';
import { ParkingPostTarget } from '../../../rent/model/parking';
import { PremisesPostTarget } from '../../../rent/model/premises';
import { OtherRentalObjectPostTarget } from '../../../rent/model/other-rental-object';
import { DwellingService } from '../../../rent/service/data/dwelling.service';
import { PremisesService } from '../../../rent/service/data/premises.service';
import { ParkingService } from '../../../rent/service/data/parking.service';
import { OtherRentalObjectService } from '../../../rent/service/data/other-rental-object.service';
import { EntrancePostTarget } from '../../../rent/model/entrance';
import { EntranceService } from '../../../rent/service/data/entrance.service';
import { ModelBase } from '../../../core/model/model-base';
import { distinctUntilChanged } from 'rxjs';
import { LanguageService } from '../../../core/service/language.service';

enum PostTargetString {
  Dwelling = 'dwelling',
  Premises = 'premises',
  Entrance = 'entrance',

  OtherRentalObject = 'otherRentalObject',
  Parking = 'parking',
  Building = 'building',

  Property = 'property',
  Company = 'company',
} // important: keys supposed to be in asc hierarchy position

interface AutocompleteValue {
  toAutocompleteValue(viewType: 'list' | 'field', culture?: string): string;
}

class PostTargetSelect<T extends AutocompleteValue> extends ModelBase {
  constructor(value: T, childScope: PostTargetChildScope = PostTargetChildScope.Full, allText?: string, id?: string) {
    super();
    this.value = value;
    this.childScope = childScope;
    this.id = id;
    this.allText = allText;
  }

  value: T;
  childScope: PostTargetChildScope;
  allText: string;

  toAutocompleteValue(viewType: 'list' | 'field', culture?: string): string {
    return this.childScope !== PostTargetChildScope.Full ? this.allText : this.value.toAutocompleteValue(viewType, culture);
  }

  toString() {
    return this.childScope !== PostTargetChildScope.Full ? this.allText : this.value.toString();
  }
}

@Component({
    selector: 'app-post-form',
    templateUrl: './post-form.component.html',
    styleUrls: ['./post-form.component.scss'],
    providers: [NavigationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PostFormComponent extends FormComponentBase<Post> {
  targetFilterFormGroup: FormGroup;

  companiesSelect: PostTargetSelect<CompanyPostTarget>[];
  propertiesSelect: PostTargetSelect<PropertyPostTarget>[];
  buildingsSelect: PostTargetSelect<BuildingPostTarget>[];
  dwellingsSelect: PostTargetSelect<DwellingPostTarget>[];
  premisesSelect: PostTargetSelect<PremisesPostTarget>[];
  parkingsSelect: PostTargetSelect<ParkingPostTarget>[];
  entrancesSelect: PostTargetSelect<EntrancePostTarget>[];
  otherRentalObjectsSelect: PostTargetSelect<OtherRentalObjectPostTarget>[];

  // companiesCache:  Map<string,  PostTargetSelect<CompanyPostTarget>[]> = new Map(); --> currently is top level component, no need to cache cos it never changes
  propertiesCache: Map<string, PostTargetSelect<PropertyPostTarget>[]> = new Map();
  buildingsCache: Map<string, PostTargetSelect<BuildingPostTarget>[]> = new Map();
  dwellingsCache: Map<string, PostTargetSelect<DwellingPostTarget>[]> = new Map();
  premisesCache: Map<string, PostTargetSelect<PremisesPostTarget>[]> = new Map();
  parkingsCache: Map<string, PostTargetSelect<ParkingPostTarget>[]> = new Map();
  entrancesCache: Map<string, PostTargetSelect<EntrancePostTarget>[]> = new Map();
  otherRentalObjectsCache: Map<string, PostTargetSelect<OtherRentalObjectPostTarget>[]> = new Map();

  constructor(
    commandDispatcher: CommandDispatcher,
    private _postService: PostService,
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    handlerResolver: StandardCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    private companyService: CompanyService,
    private _propertyService: PropertyService,
    private _buildingService: BuildingService,
    private _dwellingService: DwellingService,
    private _premisesService: PremisesService,
    private _parkingService: ParkingService,
    private _otherRentalObjectService: OtherRentalObjectService,
    private _languageService: LanguageService,
    private _entranceService: EntranceService
  ) {
    super(activatedRoute, _postService, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  get companyHasValue() {
    const selectedCompany = this.targetFilterFormGroup?.controls[PostTargetString.Company].value;
    return selectedCompany && selectedCompany.value instanceof CompanyPostTarget;
  }

  get propertyHasValue() {
    const selectedProperty = this.targetFilterFormGroup?.controls[PostTargetString.Property].value;
    return selectedProperty && selectedProperty.value instanceof PropertyPostTarget;
  }

  get buildingHasValue() {
    const selectedBuilding = this.targetFilterFormGroup?.controls[PostTargetString.Building].value;
    return selectedBuilding && selectedBuilding.value instanceof BuildingPostTarget;
  }

  protected createModel(): Post {
    return new Post();
  }

  protected getFormGroup(): FormGroup {
    this.targetFilterFormGroup = this._fb.group({
      company: this._fc.create('RENTAL_OBJECT_FORM.COMPANY_LABEL', ''),
      property: this._fc.create('RENTAL_OBJECT_FORM.PROPERTY_LABEL', ''),
      building: this._fc.create('DWELLING_FORM.BUILDING_LABEL', ''),
      dwelling: this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_DWELLING', ''),
      parking: this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_PARKING', ''),
      premises: this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_PREMISES', ''),
      entrance: this._fc.create('ENTRANCE.ENTRANCE', ''),
      otherRentalObject: this._fc.create('ARTICLE.RENTAL_OBJECT_CATEGORY_OTHER', ''),
    });

    const postTargetsArray = this._fb.array([]);
    if (this.dataContext?.postTargets?.length > 0) {
      this.dataContext?.postTargets.forEach(pt => {
        for (const postTargetString of Object.values(PostTargetString)) {
          if (pt[postTargetString]) {
            (postTargetsArray as FormArray).push(
              this._fb.group({
                id: pt.id,
                [postTargetString]: pt[postTargetString],
                childScope: pt.childScope,
              })
            );
            break;
          }
        }
      });
    }

    return this._fb.group({
      content: this._fc.createWithOptions('POST.CONTENT', this.dataContext.content, { length: 2000, required: true }),
      publishFrom: this._fb.group({}),
      publishTo: this._fb.group({}),
      sortOrder: this._fc.create('POST.SORT_ORDER', this.dataContext.sortOrder),
      title: this._fc.createWithOptions('POST.TITLE', this.dataContext.title, { length: 250, required: true }),
      postTargets: postTargetsArray,
      deletedPostTargetIds: this._fb.array([])
    });
  }

  protected init(data: Post) {
    if (data.postTargets?.length) {
      data.postTargets = data.postTargets.map(x => new PostTarget(x));
    }

    super.init(data);
    if (this.dataContext.isNew) {
      this._postService.getList().subscribe(
        data => this.form.controls['sortOrder'].setValue(data.length + 1),
        error => this.messageService.showError(error)
      );
    }

    // Download autofill data
    this.AddSubscription(
      this.companyService.getPostTargetModels().subscribe(data => {
        this.companiesSelect = data.map(x => {
          return new PostTargetSelect(x);
        });
        this.targetFilterFormGroup.controls[PostTargetString.Company].setValue(new PostTargetSelect(data[0]) || '');
      })
    );

    // Company Change
    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Company].valueChanges.pipe(distinctUntilChanged()).subscribe(selectedItem => {
        if (selectedItem?.value) {
          if (this.propertiesCache.has(selectedItem.value.id)) {
            this.propertiesSelect = this.propertiesCache.get(selectedItem.value.id);
            this.targetFilterFormGroup.controls[PostTargetString.Property].setValue('');
            this._resetPropertySelectors();
            this._resetRentalObjectSelectors();
          } else {
            this._propertyService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
              this.propertiesSelect = data.map(x => {
                return new PostTargetSelect(x);
              });
              this.propertiesCache.set(selectedItem.value.id, this.propertiesSelect);
              this.targetFilterFormGroup.controls[PostTargetString.Property].setValue('');
              this._resetPropertySelectors();
              this._resetRentalObjectSelectors();
            });
          }
        }
      })
    );

    // Property Change
    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Property].valueChanges.pipe(distinctUntilChanged()).subscribe(selectedItem => {
        if (selectedItem?.value) {
          if (selectedItem.value instanceof PropertyPostTarget) {
            // Buildings update
            if (this.buildingsCache.has(selectedItem.value.id)) {
              this.targetFilterFormGroup.controls[PostTargetString.Building].setValue('');
              this.buildingsSelect = this.buildingsCache.get(selectedItem.value.id);
            } else {
              this._buildingService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
                this.targetFilterFormGroup.controls[PostTargetString.Building].setValue('');
                this.buildingsSelect = [
                  this._createAllItem(PostTargetString.Building) as PostTargetSelect<BuildingPostTarget>,
                  ...data.map(x => {
                    return new PostTargetSelect(x);
                  }),
                ];
                this.buildingsCache.set(selectedItem.value.id, this.buildingsSelect);
              });
            }

            // Other rental object updates
            if (this.otherRentalObjectsCache.has(selectedItem.value.id)) {
              this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].setValue('');
              this.otherRentalObjectsSelect = this.otherRentalObjectsCache.get(selectedItem.value.id);
            } else {
              this._otherRentalObjectService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
                this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].setValue('');
                this.otherRentalObjectsSelect = [
                  this._createAllItem(PostTargetString.OtherRentalObject) as PostTargetSelect<OtherRentalObjectPostTarget>,
                  ...data.map(x => {
                    return new PostTargetSelect(x);
                  }),
                ];
                this.otherRentalObjectsCache.set(selectedItem.value.id, this.otherRentalObjectsSelect);
              });
            }

            // Parking updates
            if (this.parkingsCache.has(selectedItem.value.id)) {
              this.targetFilterFormGroup.controls[PostTargetString.Parking].setValue('');
              this.parkingsSelect = this.parkingsCache.get(selectedItem.value.id);
            } else {
              this._parkingService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
                this.targetFilterFormGroup.controls[PostTargetString.Parking].setValue('');
                this.parkingsSelect = [
                  this._createAllItem(PostTargetString.Parking) as PostTargetSelect<ParkingPostTarget>,
                  ...data.map(x => {
                    return new PostTargetSelect(x);
                  }),
                ];
                this.parkingsCache.set(selectedItem.value.id, this.parkingsSelect);
              });
            }
          }
        } else {
          this._resetPropertySelectors();
          this._resetRentalObjectSelectors();
        }
      })
    );

    // Building Change
    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Building].valueChanges.pipe(distinctUntilChanged()).subscribe(selectedItem => {
        this._resetRentalObjectSelectors();
        if (selectedItem?.value) {
          this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].disable({ emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Parking].disable({ emitEvent: false });
          if (selectedItem.value instanceof BuildingPostTarget) {
            // Dwelling change
            if (this.dwellingsCache.has(selectedItem.value.id)) {
              this.dwellingsSelect = this.dwellingsCache.get(selectedItem.value.id);
            } else {
              this._dwellingService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
                this.dwellingsSelect = [
                  this._createAllItem(PostTargetString.Dwelling) as PostTargetSelect<DwellingPostTarget>,
                  ...data.map(x => {
                    return new PostTargetSelect(x);
                  }),
                ];
                this.dwellingsCache.set(selectedItem.value.id, this.dwellingsSelect);
              });
            }

            // Premises  change
            if (this.premisesCache.has(selectedItem.value.id)) {
              this.premisesSelect = this.premisesCache.get(selectedItem.value.id);
            } else {
              this._premisesService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
                this.premisesSelect = [
                  this._createAllItem(PostTargetString.Premises) as PostTargetSelect<PremisesPostTarget>,
                  ...data.map(x => {
                    return new PostTargetSelect(x);
                  }),
                ];
                this.premisesCache.set(selectedItem.value.id, this.premisesSelect);
              });
            }

            // Entrance change
            if (this.entrancesCache.has(selectedItem.value.id)) {
              this.entrancesSelect = this.entrancesCache.get(selectedItem.value.id);
            } else {
              this._entranceService.getPostTargetModels(selectedItem.value.id).subscribe(data => {
                this.entrancesSelect = [
                  this._createAllItem(PostTargetString.Entrance) as PostTargetSelect<EntrancePostTarget>,
                  ...data.map(x => {
                    return new PostTargetSelect(x);
                  }),
                ];
                this.entrancesCache.set(selectedItem.value.id, this.entrancesSelect);
              });
            }
          }
        } else {
          this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].enable({ emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Parking].enable({ emitEvent: false });
        }
      })
    );

    // Other rental object change
    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].valueChanges.subscribe(selectedItem => {
        if (selectedItem?.value) {
          this.targetFilterFormGroup.controls[PostTargetString.Building].disable({ emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Parking].disable({ emitEvent: false });
        } else {
          this.targetFilterFormGroup.controls[PostTargetString.Building].enable({ emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Parking].enable({ emitEvent: false });
        }
      })
    );

    // Parking change
    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Parking].valueChanges.subscribe(selectedItem => {
        if (selectedItem?.value) {
          this.targetFilterFormGroup.controls[PostTargetString.Building].disable({ emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].disable({ emitEvent: false });
        } else {
          this.targetFilterFormGroup.controls[PostTargetString.Building].enable({ emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].enable({ emitEvent: false });
        }
      })
    );

    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Dwelling].valueChanges.subscribe(selectedItem => {
        if (selectedItem) {
          this.targetFilterFormGroup.controls[PostTargetString.Premises].setValue('', { emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Entrance].setValue('', { emitEvent: false });
        }
      })
    );

    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Premises].valueChanges.subscribe(selectedItem => {
        if (selectedItem) {
          this.targetFilterFormGroup.controls[PostTargetString.Dwelling].setValue('', { emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Entrance].setValue('', { emitEvent: false });
        }
      })
    );

    this.AddSubscription(
      this.targetFilterFormGroup.controls[PostTargetString.Entrance].valueChanges.subscribe(selectedItem => {
        if (selectedItem) {
          this.targetFilterFormGroup.controls[PostTargetString.Dwelling].setValue('', { emitEvent: false });
          this.targetFilterFormGroup.controls[PostTargetString.Premises].setValue('', { emitEvent: false });
        }
      })
    );
  }

  applyTarget(): void {
    const postTargetsArray = this.form.get('postTargets') as FormArray;
    for (const postTargetString of Object.values(PostTargetString)) {
      const selectedTarget = this.targetFilterFormGroup.controls[postTargetString].value;
      if (selectedTarget?.value?.id) {
        const postTarget =
          selectedTarget.childScope !== PostTargetChildScope.Full ? this._getPostTargetParent(postTargetString) : postTargetString;
        const postTargetFormGroup = this._fb.group({
          id: [null],
          [postTarget]: selectedTarget.value,
          childScope: selectedTarget.childScope,
        });

        postTargetsArray.push(postTargetFormGroup);
        this.form.markAsDirty();
        return;
      }
    }
  }

  protected prepareSave(): Post {
    const model = super.prepareSave();

    return model;
  }

  private _resetRentalObjectSelectors() {
    this.targetFilterFormGroup.controls[PostTargetString.Dwelling].setValue('');
    this.targetFilterFormGroup.controls[PostTargetString.Premises].setValue('');
    this.targetFilterFormGroup.controls[PostTargetString.Entrance].setValue('');
  }

  private _resetPropertySelectors() {
    this.targetFilterFormGroup.controls[PostTargetString.Building].setValue('');
    this.targetFilterFormGroup.controls[PostTargetString.OtherRentalObject].setValue('');
    this.targetFilterFormGroup.controls[PostTargetString.Parking].setValue('');
  }

  private _getPostTargetParent(postTargetString: string) {
    switch (postTargetString) {
      case PostTargetString.Property:
        return PostTargetString.Company;
      case PostTargetString.Building:
      case PostTargetString.Parking:
      case PostTargetString.OtherRentalObject:
        return PostTargetString.Property;
      case PostTargetString.Dwelling:
      case PostTargetString.Premises:
      case PostTargetString.Entrance:
        return PostTargetString.Building;
      default:
        throw new Error(`string '${postTargetString}' has no parent-child connections`); // impossible case without programming mistake BUT
    }
  }

  private _createAllItem(target: string) {
    // let selectedProperty, selectedBuilding;
    switch (target) {
      case PostTargetString.Company:
        // Could be modified at the future
        break;
      case PostTargetString.Property:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Company].value.value,
          PostTargetChildScope.Property,
          this._languageService.instant('ALL_TEXT')
        );
      case PostTargetString.Building:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Property].value.value,
          PostTargetChildScope.Building,
          this._languageService.instant('ALL_TEXT')
        );
      case PostTargetString.OtherRentalObject:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Property].value.value,
          PostTargetChildScope.OtherRentalObject,
          this._languageService.instant('ALL_TEXT')
        );
      case PostTargetString.Parking:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Property].value.value,
          PostTargetChildScope.Parking,
          this._languageService.instant('ALL_TEXT')
        );
      case PostTargetString.Dwelling:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Building].value.value,
          PostTargetChildScope.Dwelling,
          this._languageService.instant('ALL_TEXT')
        );
      case PostTargetString.Premises:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Building].value.value,
          PostTargetChildScope.Premises,
          this._languageService.instant('ALL_TEXT')
        );
      case PostTargetString.Entrance:
        return new PostTargetSelect(
          this.targetFilterFormGroup.controls[PostTargetString.Building].value.value,
          PostTargetChildScope.Entrance,
          this._languageService.instant('ALL_TEXT')
        );
      default:
        throw new Error('unrecognizable field for create All-item entity'); // impossible case without programming mistake BUT
    }
  }

  deleteTarget(index: number) {
    const postTargets = this.form.get('postTargets') as FormArray;
    const targetControl = postTargets.at(index) as FormGroup;
    const deletedPostTargetId = targetControl.get('id')?.value;

    // Remove from postTargets
    postTargets.removeAt(index);

    // Add to deletedPostTargetIds if id exists
    if (deletedPostTargetId) {
      const deletedPostTargetIds = this.form.get('deletedPostTargetIds') as FormArray;
      deletedPostTargetIds.push(new FormControl(deletedPostTargetId));
    }

    this.form.markAsDirty();
  }

  getPostTargetDescription(target) {
    for (const postTargetString of Object.values(PostTargetString)) {
      const targetValue = target[postTargetString];
      const targetFlag = target.childScope;
      if (targetValue) {
        const { key, params } = targetValue.postTargetDescription();
        if (key && params) {
          return targetFlag
            ? `${this._languageService.instant(key, params)} ${this._getChildScopeText(targetFlag)}`
            : this._languageService.instant(key, params);
        } else {
          break;
        }
      }
    }

    return this._languageService.instant('POST.POST_TARGET_DESCRIPTION.EMPTY'); // impossible case without programming mistake BUT
  }

  private _getChildScopeText(childScope: PostTargetChildScope) {
    switch (childScope) {
      case PostTargetChildScope.Company:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_COMPANIES');
      case PostTargetChildScope.Property:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_PROPERTIES');
      case PostTargetChildScope.Building:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_BUILDINGS');
      case PostTargetChildScope.Parking:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_PARKINGS');
      case PostTargetChildScope.OtherRentalObject:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_OTHER_RENTAL_OBJECTS');
      case PostTargetChildScope.Dwelling:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_DWELLINGS');
      case PostTargetChildScope.Premises:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_PREMISES');
      case PostTargetChildScope.Entrance:
        return this._languageService.instant('POST.POST_TARGET_CHILD_SCOPE.ALL_ENTRANCES');
      default:
        return '';
    }
  }
}
